/* eslint-disable jsx-a11y/anchor-is-valid */
import {createContext, useEffect} from 'react'
import {Outlet, Route, Routes} from 'react-router-dom'
import {toAbsoluteUrl} from '../../../_metronic/helpers'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {
  faUsers,
  faGears,
  faChartLine,
  faShieldHalved,
  faQuoteLeft,
  faQuoteRight,
  faMoon,
  faSun,
} from '@fortawesome/free-solid-svg-icons'
import {ForgotPassword} from './components/ForgotPassword'
import {Login} from './components/Login'
import {Registration} from './components/Registration'
import ResetPassword from './components/ResetPassword'
import ResetPassword1 from './components/ResetPassword1'
import {useThemeMode} from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider'

export const DarkModeContext = createContext(false)

const AuthLayout = () => {
  const {mode, systemMode, updateMode} = useThemeMode()
  const isDarkMode = mode === 'dark' || (mode === 'system' && systemMode === 'dark')
  
  useEffect(() => {
    document.body.style.backgroundImage = 'none'
    return () => {}
  }, [])
  
  // Add responsive styles
  useEffect(() => {
    const style = document.createElement('style')
    style.textContent = `
      @media (max-width: 768px) {
        .mobile-hide {
          display: none !important;
        }
        .mobile-full-width {
          width: calc(100% - 32px) !important;
          max-width: none !important;
        }
        .mobile-center {
          justify-content: center !important;
        }
        .mobile-padded {
          padding: 24px !important;
        }
      }
      @media (max-width: 480px) {
        .mobile-padded {
          padding: 20px !important;
        }
      }
    `
    document.head.appendChild(style)
    return () => {
      document.head.removeChild(style)
    }
  }, [])

  return (
    <div
      className='d-flex flex-column flex-column-fluid'
      style={{
        height: '100vh',
        position: 'relative',
        overflow: 'hidden',
        background: isDarkMode ? '#0F172A' : 'transparent',
      }}
    >
      {/* Theme Toggle Button */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        zIndex: 10,
      }}>
        <button
          onClick={() => updateMode(isDarkMode ? 'light' : 'dark')}
          style={{
            padding: '12px',
            borderRadius: '50%',
            background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.9)',
            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)'
          }}
        >
          <FontAwesomeIcon
            icon={isDarkMode ? faSun : faMoon}
            style={{
              color: isDarkMode ? '#FCD34D' : '#3B82D0',
              fontSize: '20px',
            }}
          />
        </button>
      </div>

      {/* Background Image with Overlay - Hide in dark mode */}
      {!isDarkMode && (
        <>
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${toAbsoluteUrl('/media/logos/Login.png')})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              zIndex: 0,
            }}
          />
          
          {/* Light blue/white overlay */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(234, 244, 255, 0.85) 0%, rgba(255, 255, 255, 0.75) 50%, rgba(224, 242, 254, 0.8) 100%)',
              zIndex: 1,
            }}
          />
          
          {/* Subtle decorative elements */}
          <div
            style={{
              position: 'absolute',
              top: '-5%',
              left: '-5%',
              width: '30%',
              height: '30%',
              background: 'radial-gradient(circle, rgba(59, 130, 208, 0.08) 0%, transparent 70%)',
              borderRadius: '50%',
              zIndex: 2,
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: '-5%',
              right: '-5%',
              width: '25%',
              height: '25%',
              background: 'radial-gradient(circle, rgba(24, 183, 176, 0.06) 0%, transparent 70%)',
              borderRadius: '50%',
              zIndex: 2,
            }}
          />
        </>
      )}
      
      {/* Main content */}
      <div className='d-flex flex-center flex-column flex-column-fluid' style={{position: 'relative', zIndex: 3, height: '100%', padding: '0'}}>
        <div className='d-flex flex-row flex-wrap w-100' style={{maxWidth: '1600px', margin: '0 auto', height: '100%', padding: '16px'}}>
          
          {/* Left side - HRMS Content */}
          <div className='d-flex flex-column justify-content-center col-lg-7 col-md-6 d-none d-md-flex mobile-hide order-2 order-md-1' style={{padding: '20px'}}>
            <div style={{padding: '16px'}}>
              {/* OfficeSIA Logo */}
              <div
                style={{
                  width: '220px',
                  height: '90px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  marginBottom: '16px',
                  overflow: 'visible',
                }}
              >
                <img
                  alt='OfficeSIA'
                  src={toAbsoluteUrl(isDarkMode ? '/media/logos/whitesialogo.png' : '/media/logos/sialogo.png')}
                  style={{
                    width: '220px',
                    height: '90px',
                    objectFit: 'contain',
                    backgroundColor: 'transparent',
                    transform: 'scale(1.5)',
                    transformOrigin: 'left center',
                  }}
                />
              </div>
                            
              {/* Main Headline */}
              <h1 style={{fontSize: '2.5rem', fontWeight: '700', color: isDarkMode ? '#F8FAFC' : '#102A56', marginBottom: '14px', lineHeight: '1.2'}}>
                People. <span style={{color: '#3B82D0'}}>Process</span>. <span style={{color: '#18B7B0'}}>Progress</span>.
              </h1>
              
              {/* Tagline */}
              <p style={{fontSize: '1.3rem', color: '#3B82D0', fontWeight: '600', marginBottom: '18px'}}>
                A Smarter HRMS for a Brighter Tomorrow.
              </p>
              
              {/* Supporting text */}
              <p style={{fontSize: '1rem', color: isDarkMode ? '#CBD5E1' : '#64748B', marginBottom: '28px', maxWidth: '480px', lineHeight: '1.5'}}>
                Empowering people, simplifying processes, and building stronger workplaces.
              </p>
              
              {/* HRMS Features - 2-column layout */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '20px',
                  marginBottom: '28px',
                }}
              >
                {/* Manage People */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  <div
                    style={{
                      width: '52px',
                      height: '52px',
                      minWidth: '52px',
                      borderRadius: '14px',
                      background: 'rgba(24, 183, 176, 0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faUsers}
                      style={{
                        color: '#18B7B0',
                        fontSize: '24px',
                      }}
                    />
                  </div>

                  <div>
                    <h3
                      style={{
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        color: isDarkMode ? '#F8FAFC' : '#102A56',
                        marginBottom: '4px',
                      }}
                    >
                      Manage People
                    </h3>

                    <p
                      style={{
                        fontSize: '0.85rem',
                        color: isDarkMode ? '#CBD5E1' : '#64748B',
                        margin: 0,
                      }}
                    >
                      Build stronger teams
                    </p>
                  </div>
                </div>

                {/* Simplify Processes */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  <div
                    style={{
                      width: '52px',
                      height: '52px',
                      minWidth: '52px',
                      borderRadius: '14px',
                      background: 'rgba(59, 130, 208, 0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faGears}
                      style={{
                        color: '#3B82D0',
                        fontSize: '24px',
                      }}
                    />
                  </div>

                  <div>
                    <h3
                      style={{
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        color: isDarkMode ? '#F8FAFC' : '#102A56',
                        marginBottom: '4px',
                      }}
                    >
                      Simplify Processes
                    </h3>

                    <p
                      style={{
                        fontSize: '0.85rem',
                        color: isDarkMode ? '#CBD5E1' : '#64748B',
                        margin: 0,
                      }}
                    >
                      Automate everyday HR tasks
                    </p>
                  </div>
                </div>

                {/* Drive Growth */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  <div
                    style={{
                      width: '52px',
                      height: '52px',
                      minWidth: '52px',
                      borderRadius: '14px',
                      background: 'rgba(139, 92, 246, 0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faChartLine}
                      style={{
                        color: '#8B5CF6',
                        fontSize: '24px',
                      }}
                    />
                  </div>

                  <div>
                    <h3
                      style={{
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        color: isDarkMode ? '#F8FAFC' : '#102A56',
                        marginBottom: '4px',
                      }}
                    >
                      Drive Growth
                    </h3>

                    <p
                      style={{
                        fontSize: '0.85rem',
                        color: isDarkMode ? '#CBD5E1' : '#64748B',
                        margin: 0,
                      }}
                    >
                      Insights for better decisions
                    </p>
                  </div>
                </div>

                {/* Secure & Reliable */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  <div
                    style={{
                      width: '52px',
                      height: '52px',
                      minWidth: '52px',
                      borderRadius: '14px',
                      background: 'rgba(242, 107, 69, 0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faShieldHalved}
                      style={{
                        color: '#F26B45',
                        fontSize: '24px',
                      }}
                    />
                  </div>

                  <div>
                    <h3
                      style={{
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        color: isDarkMode ? '#F8FAFC' : '#102A56',
                        marginBottom: '4px',
                      }}
                    >
                      Secure & Reliable
                    </h3>

                    <p
                      style={{
                        fontSize: '0.85rem',
                        color: isDarkMode ? '#CBD5E1' : '#64748B',
                        margin: 0,
                      }}
                    >
                      Your data, our priority
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Motivational Quote */}
              <div
                style={{
                  marginTop: '20px',
                  padding: '20px 24px',
                  background: isDarkMode ? 'rgba(30, 41, 59, 0.6)' : 'rgba(255, 255, 255, 0.55)',
                  borderRadius: '14px',
                  border: isDarkMode ? '1px solid rgba(59, 130, 208, 0.3)' : '1px solid rgba(59, 130, 208, 0.15)',
                  backdropFilter: 'blur(8px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '14px',
                }}
              >
                <FontAwesomeIcon
                  icon={faQuoteLeft}
                  style={{
                    color: '#3B82D0',
                    fontSize: '20px',
                    marginTop: '-12px',
                  }}
                />

                <p
                  style={{
                    fontSize: '1.05rem',
                    fontStyle: 'italic',
                    color: isDarkMode ? '#F8FAFC' : '#102A56',
                    fontWeight: '600',
                    margin: 0,
                    textAlign: 'center',
                    lineHeight: '1.5',
                    letterSpacing: '0.2px',
                  }}
                >
                  Empowering People. Enabling Progress. Building Stronger Organizations.
                </p>
                <FontAwesomeIcon
                  icon={faQuoteRight}
                  style={{color: '#3B82D0', fontSize: '20px', marginTop: '12px'}}
                />
              </div>
            </div>
          </div>
          
          {/* Right side - Login Card */}
          <div className='d-flex flex-column justify-content-center col-lg-5 col-md-6 col-12 order-1 order-md-2' style={{padding: '20px'}}>
            <div 
              className='mobile-full-width mobile-padded'
              style={{
                background: isDarkMode ? '#1E293B' : '#FFFFFF',
                borderRadius: '22px',
                boxShadow: isDarkMode ? '0 12px 48px rgba(0, 0, 0, 0.3)' : '0 12px 48px rgba(16, 42, 86, 0.15)',
                border: isDarkMode ? '1px solid rgba(59, 130, 208, 0.2)' : '1px solid rgba(59, 130, 208, 0.08)',
                padding: '36px',
                width: '100%',
                maxWidth: '580px',
                margin: '0 auto',
              }}
            >
              <DarkModeContext.Provider value={isDarkMode}>
                <Outlet />
              </DarkModeContext.Provider>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div style={{marginTop: 'auto', padding: '16px', textAlign: 'center', color: isDarkMode ? '#CBD5E1' : '#64748B', fontSize: '0.9rem'}}>
          <p style={{margin: 0}}>© 2026 OfficeSIA. All rights reserved.</p>
          <p style={{margin: '6px 0 0 0', fontWeight: '500', color: '#3B82D0'}}>People | Process | Progress</p>
        </div>
      </div>
    </div>
  )
}

const AuthPage = () => (
  <Routes>
    <Route element={<AuthLayout />}>
      <Route path='/login' element={<Login />} />
      <Route path='/registration' element={<Registration />} />
      <Route path='/forgot-password' element={<ForgotPassword />} />
      <Route path='/resetPassword' element={<ResetPassword />} />

      <Route index element={<Login />} />
    </Route>
  </Routes>
)

export {AuthPage}
